import { initRouter, onRoute, getCurrentRoute } from './router';
import type { Route } from './router';
import { initConnectionStatus } from './components/connection-status';
import { initVisibilityManager, onVisibilityChange } from './components/visibility-manager';
import { applyTheme, getTheme } from './components/theme-toggle';

// Apply saved theme on init
applyTheme(getTheme());
import { acquireWakeLock, releaseWakeLock } from './utils/wake-lock';
import { renderJoinView } from './views/join-view';
import { renderLobbyView, destroyLobbyView } from './views/lobby-view';
import { renderLocationView, destroyLocationView } from './views/location-view';
import { renderChatView, destroyChatView } from './views/chat-view';
import { renderResultsView, destroyResultsView } from './views/results-view';
import { renderSpectatorView, destroySpectatorView } from './views/spectator-view';
import { renderLeaderboardView } from './views/leaderboard-view';
import { restorePersistedState, getState } from './store';
import { navigate } from './router';
import { decideRestoreRoute } from './utils/restore-route';
import { connectTeamWs } from './api/team-sync';
import { startChatPolling, stopChatPolling } from './utils/chat-poll';
import { clearAllGameData, loadPersistedState } from './utils/state-persistence';
import { showSessionExpiredToast } from './components/team-event-toast';
import { prefetchAddressDb } from './data/address-service';

const app = document.getElementById('app')!;
let currentDestroy: (() => void) | null = null;

// Routes where screen must stay on (active gameplay)
const WAKE_LOCK_ROUTES: Set<Route> = new Set(['riddle', 'chat', 'lobby', 'spectator']);
// Session 62: routes where background chat polling should be active so the
// "Чат" button badge reflects new organizer messages while the player is on
// a location/lobby screen. Stops on join/results/leaderboard — nothing to
// badge there.
const CHAT_POLL_ROUTES: Set<Route> = new Set(['riddle', 'lobby', 'chat']);

function setView(render: (el: HTMLElement) => void | Promise<void>, destroy?: () => void): void {
  // Cleanup previous view
  if (currentDestroy) currentDestroy();
  currentDestroy = destroy || null;
  app.innerHTML = '';
  render(app);

  // Router-level wake lock management
  const route = getCurrentRoute();
  if (WAKE_LOCK_ROUTES.has(route)) {
    acquireWakeLock();
  } else {
    releaseWakeLock();
  }

  // Router-level chat polling — bad-case protection is inside chat-poll
  // (skips fetch when canChat/gameCode missing or player is currently on
  // /chat). startChatPolling is idempotent, so it's safe to call every swap.
  if (CHAT_POLL_ROUTES.has(route)) {
    startChatPolling();
  } else {
    stopChatPolling();
  }
}

// Route handlers
onRoute('join', () => setView(renderJoinView));
onRoute('lobby', () => setView(renderLobbyView, destroyLobbyView));
onRoute('riddle', () => setView(
  (el) => { renderLocationView(el); },
  () => destroyLocationView()
));
onRoute('chat', () => setView(
  (el) => { renderChatView(el); },
  destroyChatView
));
onRoute('results', () => setView(renderResultsView, destroyResultsView));
onRoute('spectator', () => setView(renderSpectatorView, destroySpectatorView));
onRoute('leaderboard', () => setView(
  (el) => { renderLeaderboardView(el); }
));

// Global error boundary — prevent white screen at 2am
window.onerror = (_msg, _src, _line, _col, err) => {
  console.error('[AutoQuest Error]', err);
  logError('global', err?.message || String(_msg));
};
window.addEventListener('unhandledrejection', (e) => {
  console.error('[AutoQuest Rejection]', e.reason);
  logError('promise', e.reason?.message || String(e.reason));
});

function logError(type: string, message: string): void {
  try {
    const errors = JSON.parse(localStorage.getItem('aq_errors') || '[]');
    errors.push({ ts: Date.now(), type, message });
    // Keep last 50 errors
    if (errors.length > 50) errors.splice(0, errors.length - 50);
    localStorage.setItem('aq_errors', JSON.stringify(errors));
  } catch { /* localStorage unavailable */ }
}

// Init
initConnectionStatus();
initVisibilityManager();

// v0.79.18 — single-source-of-truth permanent auth-failure handler.
// Fired by:
//   - request() helper / reconcile path-ы on REST 401/403 (G1)
//   - ReconnectingWebSocket on close.code=1008 (G2)
// Idempotent via window.__authFailureHandled (clearAllGameData resets it).
window.addEventListener('aq:auth-permanent-failure', () => {
  if (window.__authFailureHandled) return;
  window.__authFailureHandled = true;
  if (currentDestroy) {
    try { currentDestroy(); } catch { /* best-effort */ }
    currentDestroy = null;
  }
  clearAllGameData();
  navigate('join');
  try { showSessionExpiredToast(); } catch { /* toast is best-effort UX */ }
});

// ═══════════════════════════════════════════════════════════════════════
// Session 69 — BOOT GUARD (runs BEFORE restorePersistedState)
//
// User-reported 2026-04-22: «вышел с autokvest.ru, через пару часов вернулся
// — редиректнуло на /app/#/results». The persisted-state blob survives
// forever without a TTL, so `decideRestoreRoute` happily returned 'results'
// on every subsequent cold boot.
//
// Codex + Claude-subagent CEO voices (session 69 /autoplan) both caught
// that the fix must be at BOOT time, not inside `decideRestoreRoute` — the
// URL hash `#/results` or `#/leaderboard` can bypass the route-decision
// function entirely via the router's hashchange handler.
//
// Boot guard logic:
//   1. If persisted snapshot says FINISHED → nuke all PWA data + force
//      hash to '#/join'. Terminal state should never survive a cold boot.
//   2. If hash is '#/results' or '#/leaderboard' but the persisted
//      snapshot isn't a valid active session → same nuke. Protects against
//      shared-device scenarios where a stale hash in history points at
//      somebody else's completed run.
//   3. Otherwise (active game F5, fresh install, legit deep-link) →
//      fall through to the normal restorePersistedState path.
//
// `aq_demo_join` is special-cased: the dashboard writes it right before
// opening `/app/#/join` for a demo, and the guard must not purge it or
// the demo handoff breaks.
// ═══════════════════════════════════════════════════════════════════════

const hasDemoJoin = !!localStorage.getItem('aq_demo_join');

(function bootGuard() {
  if (hasDemoJoin) return; // demo handoff in flight — let join-view consume it

  const snapshot = loadPersistedState();
  const hash = window.location.hash;
  const terminalHash = hash === '#/results' || hash === '#/leaderboard';

  // Case 1: snapshot is terminal → always wipe.
  if (snapshot && snapshot.status === 'FINISHED') {
    clearAllGameData();
    window.location.hash = '#/join';
    return;
  }

  // Case 2: terminal hash but no valid active snapshot → wipe to prevent
  // rendering leaderboard-view / results-view with empty/foreign state.
  if (terminalHash && (!snapshot || !snapshot.gameCode || !snapshot.playerId || snapshot.status !== 'ACTIVE')) {
    clearAllGameData();
    window.location.hash = '#/join';
  }
})();

// Restore persisted state if available (survives browser crashes).
//
// Session 53 post-ship — skip the restore branch if `aq_demo_join` is in
// localStorage. The dashboard writes that key just before opening
// `/app/#/join` for a fresh demo. If we navigate on stale persisted
// state first, `renderJoinView` never runs and the demo payload sits
// unconsumed while the user plays the OLD game with old
// `demoSpeedMultiplier=1` (user-reported 2026-04-18: "запустил демо —
// время не ускоренное"). Let join-view run; it'll consume
// `aq_demo_join`, overwrite state with the new game (including the
// freshly-returned `demoSpeedMultiplier=50`), and call `navigate('riddle')`
// itself on the `res.gameStarted` branch.
const restored = restorePersistedState();
if (restored && !hasDemoJoin) {
  const target = decideRestoreRoute(getState());
  if (target) navigate(target);
  // Session 53 /investigate — re-open the team WebSocket after restore.
  // `connectTeamWs` is otherwise only called from `join-view.ts` on
  // fresh join, so after F5 the player runs without any live org-event
  // channel. The 5-second `orgAdvancePoll` catches up eventually, but
  // live advance / finish / geo-verified / caught-up events go silent
  // until the next poll — reading as "org action didn't work" in the
  // user-reported 2026-04-18 bug. Restoring the socket here makes live
  // events work immediately post-F5. Spectators and legacy-solo
  // (no teamId) are silently no-op'd inside `connectTeamWs`.
  const s = getState();
  if (s.gameCode && s.playerId && s.playerToken) {
    connectTeamWs(s.gameCode, s.playerId, s.playerToken);
  }
  // v0.79.41 — warm the FIAS SW cache the moment a player session is
  // restored. 10.4 MB blob downloads in the background (priority:'low'),
  // SW writes to `fias-db` cache. By the time the player reaches geo-input
  // mid-quest, the bytes are already on disk — zero spinner. Spectators
  // skipped (never enter addresses).
  if (s.gameCode && s.playerId && s.playerRole === 'PLAYER') {
    prefetchAddressDb();
  }
  // Otherwise let the router handle it (will go to whatever hash is set)
}

// Handle background/foreground for WebSocket reconnection + SW staleness check.
//
// SW staleness: long-lived player tabs (2-3 hr quests) hold the original SW
// + cached bundle across deploys. The dashboard org served fresh server-render
// while the player kept old PWA code → render divergence. Live trigger
// 2026-05-03 game 983541 «Бонус -20 vs -11». Throttle-gate at 5 min so we
// don't spam the SW update on every quick swipe / lockscreen tap.
let lastSwUpdateCheck = 0;
const SW_UPDATE_THROTTLE_MS = 5 * 60_000;
onVisibilityChange((visible) => {
  if (!visible) return;
  if (!('serviceWorker' in navigator)) return;
  const now = Date.now();
  if (now - lastSwUpdateCheck < SW_UPDATE_THROTTLE_MS) return;
  lastSwUpdateCheck = now;
  navigator.serviceWorker.getRegistration()
    .then((r) => r?.update())
    .catch(() => { /* best-effort */ });
});

// PWA install prompt
let deferredInstallPrompt: Event & { prompt?: () => void; userChoice?: Promise<{ outcome: string }> } | null = null;

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredInstallPrompt = e as typeof deferredInstallPrompt;
  // Show install button if on join screen
  const btn = document.getElementById('pwa-install-btn');
  if (btn) btn.style.display = '';
});

window.addEventListener('appinstalled', () => {
  deferredInstallPrompt = null;
  const btn = document.getElementById('pwa-install-btn');
  if (btn) btn.style.display = 'none';
});

export function getInstallPrompt() { return deferredInstallPrompt; }
export function clearInstallPrompt() { deferredInstallPrompt = null; }

// Register Service Worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/app/sw.js').catch((err) => {
      console.error('SW registration failed:', err);
    });
  });
}

// Start router
initRouter();
